import React from 'react';
import { motion } from 'framer-motion';
import { ArrowUpRight, MapPin, Calendar, Zap, TrendingUp } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';

const WorkExperienceCard = ({ experience, index }) => {
  const { t } = useLanguage();
  const {
    companyDisplay,
    title,
    location,
    duration,
    status,
    summary,
    tags,
    metrics,
    id
  } = experience;

  return (
    <motion.article
      className="group relative"
      initial={{ opacity: 0, y: 40 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.6, delay: index * 0.08, ease: [0.21, 0.47, 0.32, 0.98] }}
    >
      {/* Card glow effect on hover */}
      <div className="absolute -inset-px rounded-3xl bg-gradient-to-b from-white/20 via-white/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />
      
      {/* Main card */}
      <Link
        to={`/experience/${id}`}
        className="relative block h-full rounded-3xl border border-white/[0.08] bg-gradient-to-b from-white/[0.05] to-transparent backdrop-blur-xl overflow-hidden transition-[background-color,border-color] duration-200 group-hover:border-white/20 group-hover:bg-white/[0.08]"
      >
        {/* Shine effect */}
        <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/[0.03] to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-1000" />
        </div>

        {/* Top accent line */}
        <div className="absolute top-0 left-6 right-6 h-px bg-gradient-to-r from-transparent via-white/20 to-transparent" />

        <div className="relative p-6">
          {/* Header */}
          <div className="flex items-start justify-between gap-4 mb-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-lg font-semibold text-white truncate">{companyDisplay}</h3>
                {status === 'current' && (
                  <span className="shrink-0 inline-flex items-center gap-1 px-2.5 py-1 text-[10px] font-bold uppercase tracking-wider bg-emerald-500/20 text-emerald-300 rounded-full border border-emerald-500/30">
                    <span className="relative flex h-1.5 w-1.5">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-emerald-400"></span>
                    </span>
                    {t.experiencePage?.card?.current || 'Current'}
                  </span>
                )}
              </div>
              <p className="text-sm font-medium text-white/60 mb-3 line-clamp-2">{title}</p>
              <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-white/40">
                <span className="flex items-center gap-1.5">
                  <MapPin className="h-3 w-3" />
                  {location}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="h-3 w-3" />
                  {duration}
                </span>
              </div>
            </div>
            
            {/* Arrow indicator */}
            <div className="shrink-0 w-10 h-10 rounded-full bg-white/5 flex items-center justify-center border border-white/10 group-hover:bg-white/10 group-hover:border-white/20 transition-[background-color,border-color] duration-200">
              <ArrowUpRight className="h-4 w-4 text-white/40 group-hover:text-white transition-colors duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transform" />
            </div>
          </div>

          {/* Divider */}
          <div className="h-px bg-gradient-to-r from-white/10 via-white/5 to-transparent mb-5" />

          {/* Summary */}
          <p className="text-sm text-white/50 mb-5 line-clamp-2 leading-relaxed">{summary}</p>

          {/* Metrics */}
          {metrics && metrics.length > 0 && (
            <div className="mb-5">
              <div className="flex items-center gap-1.5 mb-3">
                <TrendingUp className="h-3 w-3 text-slate-400" />
                <span className="text-[10px] font-semibold uppercase tracking-wider text-white/40">
                  {t.experiencePage?.card?.keyMetrics || 'Key Metrics'}
                </span>
              </div>
              <div className="flex flex-wrap gap-2">
                {metrics.slice(0, 3).map((metric, idx) => (
                  <span
                    key={idx}
                    className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium bg-gradient-to-r from-slate-500/20 to-slate-600/10 text-slate-200 rounded-lg border border-slate-500/20"
                  >
                    <Zap className="h-3 w-3 text-slate-400" />
                    {metric}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Tags */}
          <div className="flex flex-wrap gap-1.5">
            {tags.slice(0, 4).map((tag, idx) => (
              <span
                key={idx}
                className="inline-flex items-center px-2.5 py-1 text-[11px] font-medium bg-white/[0.03] text-white/40 rounded-md border border-white/[0.06] group-hover:bg-white/[0.06] group-hover:text-white/60 transition-colors duration-300"
              >
                {tag}
              </span>
            ))}
            {tags.length > 4 && (
              <span className="inline-flex items-center px-2 py-1 text-[11px] font-medium text-white/30">
                +{tags.length - 4}
              </span>
            )}
          </div>
        </div>

        {/* Bottom hover indicator */}
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-slate-400/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </Link>
    </motion.article>
  );
};

export default WorkExperienceCard;
